/**
 * Bundled fallback catalog — ships inside the JS bundle so the "By Make &
 * Model" tab works instantly even if the CRM /services/catalog endpoint
 * is down, slow, or not yet built. The moment CRM ships, the live catalog
 * takes precedence (see CarServiceLookupWidget).
 *
 * Covers ~90% of Indian passenger-car searches (top 13 brands, top 5-12
 * models each). Keep this list editorially short — the goal is "zero blank
 * screens on ad click", not exhaustive. Long-tail cars still fall back to
 * "Get a quote" via WhatsApp.
 *
 * `body_class` maps to the pricing tier (hatchback, sedan, compact_suv,
 * mid_suv, premium) used by the fallback-price heuristic in the widget
 * if /services/pricing/lookup is also unavailable.
 */

export const CAR_BRANDS_FALLBACK = [
  {
    id: "maruti-suzuki", name: "Maruti Suzuki", color: "#0066B3", logo: "/brand-logos/maruti-suzuki.svg",
    models: [
      { id: "swift",        name: "Swift",        body_class: "hatchback",   fuels: ["petrol", "cng"] },
      { id: "baleno",       name: "Baleno",       body_class: "hatchback",   fuels: ["petrol", "cng"] },
      { id: "alto",         name: "Alto K10",     body_class: "hatchback",   fuels: ["petrol", "cng"] },
      { id: "wagonr",       name: "WagonR",       body_class: "hatchback",   fuels: ["petrol", "cng"] },
      { id: "dzire",        name: "Dzire",        body_class: "sedan",       fuels: ["petrol", "cng"] },
      { id: "brezza",       name: "Brezza",       body_class: "compact_suv", fuels: ["petrol", "cng"] },
      { id: "ertiga",       name: "Ertiga",       body_class: "mid_suv",     fuels: ["petrol", "cng"] },
      { id: "grand-vitara", name: "Grand Vitara", body_class: "mid_suv",     fuels: ["petrol"] },
      { id: "fronx",        name: "Fronx",        body_class: "compact_suv", fuels: ["petrol", "cng"] },
      { id: "celerio",      name: "Celerio",      body_class: "hatchback",   fuels: ["petrol", "cng"] },
      { id: "ignis",        name: "Ignis",        body_class: "hatchback",   fuels: ["petrol"] },
      { id: "s-presso",     name: "S-Presso",     body_class: "hatchback",   fuels: ["petrol", "cng"] },
    ],
  },
  {
    id: "hyundai", name: "Hyundai", color: "#002D62", logo: "/brand-logos/hyundai.svg",
    models: [
      { id: "i20",     name: "i20",     body_class: "hatchback",   fuels: ["petrol"] },
      { id: "creta",   name: "Creta",   body_class: "mid_suv",     fuels: ["petrol", "diesel"] },
      { id: "venue",   name: "Venue",   body_class: "compact_suv", fuels: ["petrol", "diesel"] },
      { id: "verna",   name: "Verna",   body_class: "sedan",       fuels: ["petrol"] },
      { id: "aura",    name: "Aura",    body_class: "sedan",       fuels: ["petrol", "cng"] },
      { id: "exter",   name: "Exter",   body_class: "compact_suv", fuels: ["petrol", "cng"] },
      { id: "tucson",  name: "Tucson",  body_class: "premium",     fuels: ["petrol", "diesel"] },
      { id: "alcazar", name: "Alcazar", body_class: "mid_suv",     fuels: ["petrol", "diesel"] },
    ],
  },
  {
    id: "tata", name: "Tata", color: "#1E5091", logo: "/brand-logos/tata.svg",
    models: [
      { id: "nexon",   name: "Nexon",   body_class: "compact_suv", fuels: ["petrol", "diesel"] },
      { id: "punch",   name: "Punch",   body_class: "compact_suv", fuels: ["petrol", "cng"] },
      { id: "tiago",   name: "Tiago",   body_class: "hatchback",   fuels: ["petrol", "cng"] },
      { id: "altroz",  name: "Altroz",  body_class: "hatchback",   fuels: ["petrol", "diesel", "cng"] },
      { id: "harrier", name: "Harrier", body_class: "premium",     fuels: ["diesel"] },
      { id: "safari",  name: "Safari",  body_class: "premium",     fuels: ["diesel"] },
      { id: "tigor",   name: "Tigor",   body_class: "sedan",       fuels: ["petrol", "cng"] },
    ],
  },
  {
    id: "mahindra", name: "Mahindra", color: "#D52B1E", logo: "/brand-logos/mahindra.svg",
    models: [
      { id: "xuv700",    name: "XUV700",    body_class: "premium",     fuels: ["petrol", "diesel"] },
      { id: "scorpio-n", name: "Scorpio N", body_class: "premium",     fuels: ["petrol", "diesel"] },
      { id: "thar",      name: "Thar",      body_class: "mid_suv",     fuels: ["petrol", "diesel"] },
      { id: "xuv300",    name: "XUV300",    body_class: "compact_suv", fuels: ["petrol", "diesel"] },
      { id: "bolero",    name: "Bolero",    body_class: "mid_suv",     fuels: ["diesel"] },
      { id: "xuv400",    name: "XUV400",    body_class: "compact_suv", fuels: ["petrol"] },
    ],
  },
  {
    id: "kia", name: "Kia", color: "#05141F", logo: "/brand-logos/kia.svg",
    models: [
      { id: "sonet",    name: "Sonet",    body_class: "compact_suv", fuels: ["petrol", "diesel"] },
      { id: "seltos",   name: "Seltos",   body_class: "mid_suv",     fuels: ["petrol", "diesel"] },
      { id: "carens",   name: "Carens",   body_class: "mid_suv",     fuels: ["petrol", "diesel"] },
      { id: "carnival", name: "Carnival", body_class: "premium",     fuels: ["diesel"] },
    ],
  },
  {
    id: "toyota", name: "Toyota", color: "#EB0A1E", logo: "/brand-logos/toyota.svg",
    models: [
      { id: "innova-crysta",  name: "Innova Crysta",  body_class: "premium",   fuels: ["diesel"] },
      { id: "innova-hycross", name: "Innova Hycross", body_class: "premium",   fuels: ["petrol"] },
      { id: "hyryder",        name: "Hyryder",        body_class: "mid_suv",   fuels: ["petrol"] },
      { id: "glanza",         name: "Glanza",         body_class: "hatchback", fuels: ["petrol", "cng"] },
      { id: "fortuner",       name: "Fortuner",       body_class: "premium",   fuels: ["petrol", "diesel"] },
      { id: "hilux",          name: "Hilux",          body_class: "premium",   fuels: ["diesel"] },
    ],
  },
  {
    id: "honda", name: "Honda", color: "#CC0000", logo: "/brand-logos/honda.svg",
    models: [
      { id: "city",     name: "City",     body_class: "sedan",   fuels: ["petrol"] },
      { id: "amaze",    name: "Amaze",    body_class: "sedan",   fuels: ["petrol"] },
      { id: "elevate",  name: "Elevate",  body_class: "mid_suv", fuels: ["petrol"] },
    ],
  },
  {
    id: "skoda", name: "Škoda", color: "#4BA82E", logo: "/brand-logos/skoda.svg",
    models: [
      { id: "kushaq",  name: "Kushaq",  body_class: "mid_suv", fuels: ["petrol"] },
      { id: "slavia",  name: "Slavia",  body_class: "sedan",   fuels: ["petrol"] },
      { id: "kodiaq",  name: "Kodiaq",  body_class: "premium", fuels: ["petrol"] },
    ],
  },
  {
    id: "volkswagen", name: "Volkswagen", color: "#1A2D4F", logo: "/brand-logos/volkswagen.svg",
    models: [
      { id: "taigun",  name: "Taigun",  body_class: "mid_suv", fuels: ["petrol"] },
      { id: "virtus",  name: "Virtus",  body_class: "sedan",   fuels: ["petrol"] },
    ],
  },
  {
    id: "mg", name: "MG", color: "#E30613", logo: "/brand-logos/mg.svg",
    models: [
      { id: "hector",  name: "Hector",  body_class: "premium", fuels: ["petrol", "diesel"] },
      { id: "astor",   name: "Astor",   body_class: "mid_suv", fuels: ["petrol"] },
      { id: "gloster", name: "Gloster", body_class: "premium", fuels: ["diesel"] },
    ],
  },
  {
    id: "renault", name: "Renault", color: "#FFCC00", logo: "/brand-logos/renault.svg",
    models: [
      { id: "kwid",   name: "Kwid",   body_class: "hatchback",   fuels: ["petrol"] },
      { id: "triber", name: "Triber", body_class: "mid_suv",     fuels: ["petrol"] },
      { id: "kiger",  name: "Kiger",  body_class: "compact_suv", fuels: ["petrol"] },
    ],
  },
  {
    id: "nissan", name: "Nissan", color: "#C3002F", logo: "/brand-logos/nissan.svg",
    models: [
      { id: "magnite", name: "Magnite", body_class: "compact_suv", fuels: ["petrol"] },
    ],
  },
  {
    id: "jeep", name: "Jeep", color: "#0E1012", logo: "/brand-logos/jeep.svg",
    models: [
      { id: "compass",  name: "Compass",  body_class: "premium", fuels: ["petrol", "diesel"] },
      { id: "meridian", name: "Meridian", body_class: "premium", fuels: ["diesel"] },
    ],
  },
  {
    id: "ford", name: "Ford", color: "#003478", logo: "/brand-logos/ford.svg",
    models: [
      { id: "ecosport",  name: "EcoSport",  body_class: "compact_suv", fuels: ["petrol", "diesel"] },
      { id: "endeavour", name: "Endeavour", body_class: "premium",     fuels: ["diesel"] },
      { id: "figo",      name: "Figo",      body_class: "hatchback",   fuels: ["petrol", "diesel"] },
      { id: "aspire",    name: "Aspire",    body_class: "sedan",       fuels: ["petrol", "diesel"] },
      { id: "freestyle", name: "Freestyle", body_class: "hatchback",   fuels: ["petrol", "diesel"] },
    ],
  },
  {
    id: "citroen", name: "Citroën", color: "#DC0428", logo: "/brand-logos/citroen.svg",
    models: [
      { id: "c3",          name: "C3",          body_class: "hatchback",   fuels: ["petrol"] },
      { id: "c3-aircross", name: "C3 Aircross", body_class: "compact_suv", fuels: ["petrol"] },
      { id: "basalt",      name: "Basalt",      body_class: "compact_suv", fuels: ["petrol"] },
      { id: "ec3",         name: "ëC3",         body_class: "hatchback",   fuels: ["electric"] },
    ],
  },
  {
    id: "datsun", name: "Datsun", color: "#B7101F", logo: "/brand-logos/datsun.svg",
    models: [
      { id: "go",       name: "GO",       body_class: "hatchback", fuels: ["petrol"] },
      { id: "go-plus",  name: "GO+",      body_class: "mid_suv",   fuels: ["petrol"] },
      { id: "redi-go",  name: "redi-GO",  body_class: "hatchback", fuels: ["petrol"] },
    ],
  },
  {
    id: "bmw", name: "BMW", color: "#0066B1", logo: "/brand-logos/bmw.svg",
    models: [
      { id: "3-series", name: "3 Series", body_class: "premium", fuels: ["petrol", "diesel"] },
      { id: "5-series", name: "5 Series", body_class: "premium", fuels: ["petrol", "diesel"] },
      { id: "x1",       name: "X1",       body_class: "premium", fuels: ["petrol", "diesel"] },
      { id: "x3",       name: "X3",       body_class: "premium", fuels: ["petrol", "diesel"] },
      { id: "x5",       name: "X5",       body_class: "premium", fuels: ["petrol", "diesel"] },
      { id: "x7",       name: "X7",       body_class: "premium", fuels: ["petrol", "diesel"] },
    ],
  },
  {
    id: "mercedes-benz", name: "Mercedes-Benz", color: "#1B1B1B", logo: "/brand-logos/mercedes-benz.svg",
    models: [
      { id: "a-class", name: "A-Class", body_class: "premium", fuels: ["petrol"] },
      { id: "c-class", name: "C-Class", body_class: "premium", fuels: ["petrol", "diesel"] },
      { id: "e-class", name: "E-Class", body_class: "premium", fuels: ["petrol", "diesel"] },
      { id: "gla",     name: "GLA",     body_class: "premium", fuels: ["petrol"] },
      { id: "glc",     name: "GLC",     body_class: "premium", fuels: ["petrol", "diesel"] },
      { id: "gle",     name: "GLE",     body_class: "premium", fuels: ["petrol", "diesel"] },
      { id: "gls",     name: "GLS",     body_class: "premium", fuels: ["petrol", "diesel"] },
    ],
  },
  {
    id: "audi", name: "Audi", color: "#BB0A30", logo: "/brand-logos/audi.svg",
    models: [
      { id: "a4", name: "A4", body_class: "premium", fuels: ["petrol"] },
      { id: "a6", name: "A6", body_class: "premium", fuels: ["petrol"] },
      { id: "q3", name: "Q3", body_class: "premium", fuels: ["petrol"] },
      { id: "q5", name: "Q5", body_class: "premium", fuels: ["petrol"] },
      { id: "q7", name: "Q7", body_class: "premium", fuels: ["petrol"] },
      { id: "q8", name: "Q8", body_class: "premium", fuels: ["petrol"] },
    ],
  },
  {
    id: "volvo", name: "Volvo", color: "#003057", logo: "/brand-logos/volvo.svg",
    models: [
      { id: "xc40", name: "XC40", body_class: "premium", fuels: ["petrol", "electric"] },
      { id: "xc60", name: "XC60", body_class: "premium", fuels: ["petrol"] },
      { id: "xc90", name: "XC90", body_class: "premium", fuels: ["petrol", "diesel"] },
      { id: "s60",  name: "S60",  body_class: "premium", fuels: ["petrol"] },
      { id: "s90",  name: "S90",  body_class: "premium", fuels: ["petrol"] },
    ],
  },
];

/**
 * Fallback pricing tiers — used only if the CRM /services/pricing/lookup
 * endpoint is unreachable. Keeps LP functional for ads in degraded state.
 *
 * Base tier assumes a Maruti/Datsun-class vehicle (multiplier 1.00). Per-brand
 * multipliers below scale these tier prices to reflect real OEM parts + labour
 * cost differences across the Indian passenger-car market.
 */
export const FALLBACK_PRICING = {
  hatchback:   { petrol: 2499, diesel: 2999, cng: 2799, electric: 2199 },
  sedan:       { petrol: 2999, diesel: 3499, cng: 3199, electric: 2599 },
  compact_suv: { petrol: 3499, diesel: 3999, cng: 3699, electric: 2999 },
  mid_suv:     { petrol: 3999, diesel: 4499, cng: 4199, electric: 3499 },
  premium:     { petrol: 4499, diesel: 4999, cng: 4699, electric: 3899 },
};

/**
 * Brand multipliers applied to FALLBACK_PRICING when the CRM API is down.
 * Derived from real Indian workshop cost data (OEM parts price index,
 * labour hours, diagnostic-tool specialisation).
 *
 * Tiers:
 *   1.00 — Budget baseline: cheapest OEM parts, highest workshop density,
 *          standard labour (Maruti Suzuki, Datsun).
 *   1.08 — Mainstream Indian/Korean/French: slightly costlier parts, same
 *          labour (Hyundai, Tata, Renault, Nissan).
 *   1.15 — Mainstream Japanese/Korean-premium/Indian-premium: higher OEM
 *          cost, common diagnostic tools (Honda, Toyota, Kia, Mahindra,
 *          Ford, MG, Citroën).
 *   1.30 — European mainstream: pricier OEM parts, some specialised
 *          tooling, VW-group VAG-COM diagnostics (Škoda, Volkswagen, Jeep).
 *   1.60 — Luxury entry: premium parts, longer labour, specialised
 *          software (Volvo).
 *   1.90 — Full luxury: extensive electronics, ISTA/ODIS diagnostics,
 *          higher labour rate (BMW, Audi).
 *   2.10 — Ultra-luxury: highest OEM cost, Mercedes STAR/XENTRY
 *          diagnostic system, specialist mechanic hourly rate.
 *
 * Missing brand → default 1.15 (mainstream Japanese-equivalent).
 */
export const BRAND_MULTIPLIER = {
  "maruti-suzuki": 1.00,
  datsun:          1.00,
  hyundai:         1.08,
  tata:            1.08,
  renault:         1.08,
  nissan:          1.08,
  honda:           1.15,
  toyota:          1.15,
  kia:             1.15,
  mahindra:        1.15,
  ford:            1.15,
  mg:              1.15,
  citroen:         1.15,
  skoda:           1.30,
  volkswagen:      1.30,
  jeep:            1.30,
  volvo:           1.60,
  bmw:             1.90,
  audi:            1.90,
  "mercedes-benz": 2.10,
};

/** Round to the nearest ₹50 so fallback prices read like a real quote. */
function roundToNearest50(amount) {
  return Math.round(amount / 50) * 50;
}

/**
 * Fallback inclusions list — mirrors the CRM `/service-packages`
 * response for GENERAL Service (see the 11-item list returned for
 * Maruti/Petrol/Hatchback). We keep this in sync so visitors see the
 * SAME service items whether the CRM is up or briefly unreachable —
 * never any item that we don't actually deliver (e.g. carwash was
 * previously listed here but isn't part of GENERAL service).
 *
 * If the CRM item list changes, update this array to match. To resync:
 *   curl "https://crm.wisedrive.in/api/public/service-packages?body_class=HATCHBACK&fuel_type=PETROL&brand=maruti-suzuki" \
 *     | jq '.packages[] | select(.category=="GENERAL") | .items[].name'
 */
export const FALLBACK_INCLUSIONS = [
  { icon: "oil",     label: "Engine Oil Replacement" },
  { icon: "filter",  label: "Oil Filter Replacement" },
  { icon: "filter",  label: "Air Filter Replacement" },
  { icon: "fluid",   label: "Wiper Fluid Top Up" },
  { icon: "fluid",   label: "Coolant Top Up (200 ml)" },
  { icon: "fluid",   label: "Brake Fluid Top Up" },
  { icon: "check",   label: "Heater / Spark Plugs Checking" },
  { icon: "filter",  label: "Fuel Filter Checking" },
  { icon: "battery", label: "Battery Water Top Up" },
  { icon: "brake",   label: "Front Brake Pads Serviced" },
  { icon: "scan",    label: "Car Scanning" },
];

/**
 * Shape-normalised catalog in the same format CRM's /services/catalog returns,
 * so the UI treats live and fallback data identically.
 */
export function buildFallbackCatalog() {
  return {
    service_type: "general_service",
    source: "bundled_fallback",
    updated_at: new Date().toISOString(),
    brands: CAR_BRANDS_FALLBACK,
  };
}

/**
 * Computes a fallback price for a vehicle when the CRM pricing endpoint
 * is unavailable. Returns the same shape as /services/pricing/lookup.
 *
 * Formula: base_tier_price × brand_multiplier, rounded to nearest ₹50.
 * If `brand` is unknown, defaults to the mainstream Japanese/Korean tier (1.15).
 */
export function buildFallbackPrice({ body_class, fuel, brand }) {
  const tier = FALLBACK_PRICING[body_class] || FALLBACK_PRICING.hatchback;
  const base = tier[fuel] ?? tier.petrol;
  const multiplier = BRAND_MULTIPLIER[brand] ?? 1.15;
  const amount = roundToNearest50(base * multiplier);
  return {
    price_id: `fallback_${brand || "unknown"}_${body_class}_${fuel}`,
    service_type: "general_service",
    amount_display: amount,
    amount: amount * 100,
    currency: "INR",
    tax_inclusive: true,
    duration_minutes: 90,
    warranty: "1,000 km / 30 days",
    inclusions: FALLBACK_INCLUSIONS,
    exclusions: ["Brake pad replacement (if needed)", "Battery (if dead)"],
    city_serviceable: true,
    source: "bundled_fallback",
    brand_multiplier: multiplier,
  };
}
